import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { LinkIcon } from '../../layout/icons';
import { normalizeYoutubeUrl, youtubeThumbnailUrl, type YoutubeVideoReference } from '../link/youtube';
import { PlayTriangleIcon, YoutubeGlyph } from './panelGlyphs';
import { msg, useMsg, type Msg } from '../../i18n/msg';

export interface InsertedYoutubeVideo extends YoutubeVideoReference {
  rawUrl: string;
}

export interface InsertLinkPanelProps {
  videos: InsertedYoutubeVideo[];
  onInsert: (video: InsertedYoutubeVideo) => void;
  onPlay: (video: InsertedYoutubeVideo) => void;
}

export function InsertLinkPanel({ videos, onInsert, onPlay }: InsertLinkPanelProps) {
  const { t } = useTranslation('panels');
  const tr = useMsg();
  const [input, setInput] = useState('');
  const [error, setError] = useState<Msg | null>(null);

  function insert() {
    const video = normalizeYoutubeUrl(input);
    if (!video) {
      setError(msg(input.trim() ? 'panels:youtube.invalid' : 'panels:youtube.emptyInput'));
      return;
    }
    setError(null);
    onInsert({ ...video, rawUrl: input.trim() });
    setInput('');
  }

  return (
    <div className="yt-pane">
      <div className="yt-pane__field">
        <LinkIcon size={13} />
        <input
          value={input}
          onChange={(event) => { setInput(event.target.value); setError(null); }}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); insert(); } }}
          placeholder={t('youtube.placeholder')}
          aria-label={t('youtube.inputLabel')}
        />
      </div>
      <p className="yt-pane__hint">youtube.com/watch?v=… · youtu.be/…</p>
      {error ? <p className="yt-pane__error" role="alert">{tr(error)}</p> : null}
      <button type="button" className="yt-pane__insert" onClick={insert}>
        <YoutubeGlyph size={12} />
        <span>{t('youtube.insert')}</span>
      </button>

      {videos.length === 0 ? (
        <p className="yt-empty">{t('youtube.empty')}</p>
      ) : (
        <section className="yt-list">
          <h3 className="yt-list__heading">{t('youtube.inserted', { total: videos.length })}</h3>
          {videos.map((video) => (
            <button type="button" className="yt-card" key={`${video.id}-${video.start}`} aria-label={t('youtube.play', { url: video.rawUrl })} title={video.rawUrl} onClick={() => onPlay(video)}>
              <span className="yt-card__thumb">
                <img src={youtubeThumbnailUrl(video.id)} alt="" />
                <span className="yt-card__scrim" aria-hidden={true} />
                <span className="yt-card__play" aria-hidden={true}><PlayTriangleIcon size={15} /></span>
              </span>
              <span className="yt-card__url">
                <YoutubeGlyph size={12} />
                <span>{video.rawUrl}</span>
              </span>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
